import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useAiStreamSse } from "@/common/hooks/useAiStreamSse";
import { useAuth } from "@/common/hooks/useAuth";
import { useToast } from "@/common/hooks/useToast";
import {
  getActiveGenerationJobs,
  getGenerationJob,
  dismissGenerationJob,
  type GenerationJobDto,
} from "@/common/services/test-gen/test-gen.api";

export type BgGenStatus = "running" | "complete" | "error";
export type BgGenMode = "manual" | "automation";

/**
 * A backgroundable AI generation "session". The backend runs generation
 * fire-and-forget and buffers SSE events by `generationId`, so the only thing we
 * must keep alive across a modal close is this snapshot + the subscription.
 *
 * Phase 2 adds durability: the backend also persists a job row per generation,
 * so this snapshot can be *rehydrated* after a hard refresh, in a second tab, or
 * once the in-memory SSE replay window has expired.
 */
export interface BgGenSession {
  generationId: string;
  mode: BgGenMode;
  folderId: number | null;
  folderName: string | null;
  status: BgGenStatus;
  progress: number;
  message: string | null;
  /** Accumulated generated scenarios (loosely typed to avoid coupling). */
  testStructure: any[];
  error: string | null;
  startedAt: number;
  /** Context needed to restore the generator view on reopen. */
  prompt?: string;
  requirementIds?: number[];
  /** True when restored from the DB (refresh / new tab / broadcast) rather than started here. */
  restored?: boolean;
}

interface BeginInit {
  generationId: string;
  mode: BgGenMode;
  folderId?: number | null;
  folderName?: string | null;
  prompt?: string;
  requirementIds?: number[];
}

type MirrorPatch = Partial<
  Pick<
    BgGenSession,
    "status" | "progress" | "message" | "testStructure" | "error"
  >
>;

/**
 * Stable actions — this context value never changes identity, so consumers that
 * only need actions (the generator, the modal) never re-render on a progress tick.
 */
interface BgGenActions {
  /** Called by the generator when a backgroundable generation starts. */
  begin: (init: BeginInit) => void;
  /** Live mirror of the owner (modal) state while it is mounted. */
  mirror: (patch: MirrorPatch) => void;
  /**
   * Signal whether the generator modal is currently mounted ("owns" the SSE).
   * When false and the session is running, the provider takes over the stream so
   * generation keeps flowing in the background.
   */
  setOwnerMounted: (mounted: boolean) => void;
  /** Clear the session (save complete / dismissed / stopped). */
  clear: () => void;
  /** Read the current session without subscribing (for one-shot mount reads). */
  getSession: () => BgGenSession | null;
}

/** Reactive session state — changes on every progress tick. Only the indicator subscribes. */
interface BgGenState {
  session: BgGenSession | null;
  resultCount: number;
}

const BgGenActionsContext = createContext<BgGenActions | null>(null);
const BgGenStateContext = createContext<BgGenState | null>(null);

const BROADCAST_CHANNEL = "autopilotqa:bg-gen";
// A "running" job whose row hasn't been touched in this long is treated as
// orphaned (e.g. the server restarted and killed the in-process generation) so
// the indicator doesn't spin forever after a refresh.
const STALE_RUNNING_MS = 10 * 60 * 1000;
const TOAST_DEDUPE_PREFIX = "bggen:toasted:";
const TOAST_DEDUPE_TTL_MS = 10 * 60 * 1000;

/**
 * Cross-tab toast de-duplication. When multiple tabs each stream the same
 * generation, only the first to reach a terminal event should toast. Backed by
 * localStorage so it works across tabs; self-pruning to stay tiny.
 */
function claimToast(generationId: string): boolean {
  try {
    const key = TOAST_DEDUPE_PREFIX + generationId;
    const now = Date.now();
    const existing = window.localStorage.getItem(key);
    if (existing && now - Number(existing) < TOAST_DEDUPE_TTL_MS) return false;
    window.localStorage.setItem(key, String(now));
    // Opportunistic prune of stale entries.
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith(TOAST_DEDUPE_PREFIX)) {
        const v = Number(window.localStorage.getItem(k));
        if (!v || now - v > TOAST_DEDUPE_TTL_MS)
          window.localStorage.removeItem(k);
      }
    }
    return true;
  } catch {
    // localStorage unavailable (private mode / SSR) — allow the toast.
    return true;
  }
}

/** Count test cases across the (loosely typed) scenario tree. */
function countTestCases(structure: any[] | undefined): number {
  if (!Array.isArray(structure)) return 0;
  return structure.reduce(
    (sum, s) => sum + (Array.isArray(s?.testCases) ? s.testCases.length : 0),
    0,
  );
}

/**
 * Apply a "standard test-gen update" (the manual / synced-prompt path) to the
 * accumulated scenario tree. Mirrors the reducer in the generator so results
 * stay correct while the modal is closed. Crawl-specific event types are
 * intentionally ignored here — those only matter during the interactive crawl
 * phase, which always has the modal open.
 */
function applyUpdate(
  prev: any[],
  type: string,
  data: any,
  meta: Record<string, any>,
): any[] {
  if (type === "initial") return Array.isArray(data) ? data : prev;
  if (type === "testCases") {
    return prev.map((s) =>
      s.id === meta?.scenarioId ? { ...s, testCases: data } : s,
    );
  }
  if (type === "steps") {
    return prev.map((s) => ({
      ...s,
      testCases: (s.testCases ?? []).map((tc: any) =>
        tc.id === meta?.testCaseId ? { ...tc, steps: data } : tc,
      ),
    }));
  }
  return prev;
}

/** Build a session snapshot from a persisted job DTO (metadata only; no result). */
function sessionFromJob(job: GenerationJobDto): BgGenSession {
  const status: BgGenStatus =
    job.status === "completed"
      ? "complete"
      : job.status === "running"
        ? "running"
        : "error";
  return {
    generationId: job.generationId,
    mode: job.mode === "automation" ? "automation" : "manual",
    folderId: job.folderId ?? null,
    folderName: job.folderName ?? null,
    status,
    progress: job.progress ?? (status === "complete" ? 100 : 0),
    message: job.message ?? null,
    testStructure: [],
    error: job.error ?? null,
    startedAt: job.createdAt ? new Date(job.createdAt).getTime() : Date.now(),
    prompt: job.prompt ?? undefined,
    requirementIds: Array.isArray(job.requirementIds)
      ? (job.requirementIds
          .map((r) => Number(r))
          .filter((n) => !Number.isNaN(n)) as number[])
      : undefined,
    restored: true,
  };
}

export function BackgroundGenerationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { showToast } = useToast();
  const { userPreferenceData } = useAuth();
  const versionId: number | string | undefined =
    userPreferenceData?.data?.versionId;

  const [session, setSession] = useState<BgGenSession | null>(null);
  const [ownerMounted, setOwnerMountedState] = useState(false);
  const ownerMountedRef = useRef(false);

  const sessionRef = useRef<BgGenSession | null>(null);
  sessionRef.current = session;

  // ── Cross-tab channel ──────────────────────────────────────────────────────
  const tabIdRef = useRef<string>(
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
  );
  const channelRef = useRef<BroadcastChannel | null>(null);

  const broadcast = useCallback(
    (kind: string, payload: Record<string, any>) => {
      try {
        channelRef.current?.postMessage({
          tabId: tabIdRef.current,
          kind,
          ...payload,
        });
      } catch {
        // channel closed / structured-clone failure — non-fatal
      }
    },
    [],
  );

  // Fetch the persisted result tree for a completed generation and merge it in.
  const hydrateResult = useCallback((generationId: string) => {
    getGenerationJob(generationId)
      .then((res) => {
        const result = (res?.data?.job as any)?.result;
        if (!Array.isArray(result)) return;
        setSession((prev) =>
          prev && prev.generationId === generationId
            ? {
                ...prev,
                testStructure: result,
                status: "complete",
                progress: 100,
              }
            : prev,
        );
      })
      .catch(() => {
        /* best-effort — indicator still shows the count from metadata */
      });
  }, []);

  const begin = useCallback(
    (init: BeginInit) => {
      const next: BgGenSession = {
        generationId: init.generationId,
        mode: init.mode,
        folderId: init.folderId ?? null,
        folderName: init.folderName ?? null,
        status: "running",
        progress: 0,
        message: null,
        testStructure: [],
        error: null,
        startedAt: Date.now(),
        prompt: init.prompt,
        requirementIds: init.requirementIds,
      };
      setSession(next);
      // The modal is open when generation starts, so it owns the stream.
      ownerMountedRef.current = true;
      setOwnerMountedState(true);
      broadcast("begin", { session: { ...next, testStructure: [] } });
    },
    [broadcast],
  );

  const mirror = useCallback((patch: MirrorPatch) => {
    setSession((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const setOwnerMounted = useCallback((mounted: boolean) => {
    ownerMountedRef.current = mounted;
    setOwnerMountedState(mounted);
  }, []);

  const clear = useCallback(() => {
    const gid = sessionRef.current?.generationId;
    setSession(null);
    ownerMountedRef.current = false;
    setOwnerMountedState(false);
    if (gid) {
      // Acknowledge server-side so it stops resurfacing on reload, and tell
      // other tabs to drop it too.
      void dismissGenerationJob(gid).catch(() => {});
      broadcast("clear", { generationId: gid });
    }
  }, [broadcast]);

  const getSession = useCallback(() => sessionRef.current, []);

  // The provider only runs its OWN subscription when the modal is closed and the
  // session is still running — otherwise the modal owns the stream (single
  // steady-state connection). `null` disconnects the hook.
  const providerGenerationId =
    session && session.status === "running" && !ownerMounted
      ? session.generationId
      : null;

  useAiStreamSse({
    generationId: providerGenerationId,
    onStatusChange: (message, progress) => {
      setSession((prev) => (prev ? { ...prev, message, progress } : prev));
    },
    onUpdate: (type, data, _gid, meta) => {
      setSession((prev) =>
        prev
          ? {
              ...prev,
              testStructure: applyUpdate(prev.testStructure, type, data, meta),
            }
          : prev,
      );
    },
    onComplete: (data) => {
      setSession((prev) => {
        if (!prev) return prev;
        const finalStructure = Array.isArray(data) ? data : prev.testStructure;
        const count = countTestCases(finalStructure);
        // Only notify when the modal is closed — an open modal shows results
        // itself — and only the first tab to finish (cross-tab de-dupe).
        if (!ownerMountedRef.current && claimToast(prev.generationId)) {
          showToast({
            title: "Test cases ready",
            description:
              count > 0
                ? `${count} test case${count === 1 ? "" : "s"} generated. Open the generator to review.`
                : "AI generation finished. Open the generator to review.",
            variant: "success",
          });
        }
        broadcast("terminal", {
          generationId: prev.generationId,
          status: "complete",
          resultCount: count,
        });
        return {
          ...prev,
          status: "complete",
          progress: 100,
          message: "Complete",
          testStructure: finalStructure,
        };
      });
    },
    onError: (message) => {
      setSession((prev) => {
        if (!prev) return prev;
        if (!ownerMountedRef.current && claimToast(prev.generationId)) {
          showToast({
            title: "AI generation failed",
            description:
              message || "Something went wrong while generating test cases.",
            variant: "error",
          });
        }
        broadcast("terminal", {
          generationId: prev.generationId,
          status: "error",
        });
        return {
          ...prev,
          status: "error",
          error: message || "Generation failed",
        };
      });
    },
  });

  // ── Rehydrate on mount (#16 refresh, #18 expired replay window) ─────────────
  // Once auth resolves, look up the requester's active/recent background jobs
  // for this workspace version and restore the most relevant one. Guarded to run
  // once and to never clobber a session started locally in the meantime.
  const didRehydrateRef = useRef(false);
  useEffect(() => {
    if (didRehydrateRef.current) return;
    if (sessionRef.current) {
      didRehydrateRef.current = true;
      return;
    }
    // Wait until auth is resolved enough to know the user/version.
    if (!userPreferenceData?.data) return;
    didRehydrateRef.current = true;

    getActiveGenerationJobs(versionId ?? null, "test_case")
      .then((res) => {
        if (sessionRef.current) return; // a local generation started meanwhile
        const jobs = res?.data?.jobs ?? [];
        if (!jobs.length) return;
        const running = jobs.find((j) => j.status === "running");
        const chosen = running ?? jobs.find((j) => j.status === "completed");
        if (!chosen) return;

        if (chosen.status === "running") {
          const updatedAt = chosen.updatedAt
            ? new Date(chosen.updatedAt).getTime()
            : 0;
          if (updatedAt && Date.now() - updatedAt > STALE_RUNNING_MS) {
            // Orphaned running row (likely a server restart) — dismiss quietly.
            void dismissGenerationJob(chosen.generationId).catch(() => {});
            return;
          }
          // Seed as running; providerGenerationId picks up and reconnects SSE
          // (backend replays buffered/completed events + streams the rest).
          setSession(sessionFromJob(chosen));
          return;
        }

        // Completed — restore metadata immediately, then fetch the result tree.
        const restored = sessionFromJob(chosen);
        restored.progress = 100;
        setSession(restored);
        if (chosen.hasResult) hydrateResult(chosen.generationId);
      })
      .catch(() => {
        /* best-effort — no background recovery available */
      });
  }, [userPreferenceData, versionId, hydrateResult]);

  // ── Cross-tab sync (#20) ────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;
    const channel = new BroadcastChannel(BROADCAST_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (evt: MessageEvent) => {
      const msg = evt.data;
      if (!msg || msg.tabId === tabIdRef.current) return;

      if (msg.kind === "begin" && msg.session) {
        const incoming = msg.session as BgGenSession;
        const cur = sessionRef.current;
        // Adopt only if we're idle or already tracking this same generation.
        // If we're busy with a *different* running gen, ignore (don't clobber).
        if (!cur || cur.generationId === incoming.generationId) {
          setSession({ ...incoming, testStructure: [], restored: true });
        }
        return;
      }

      if (msg.kind === "terminal") {
        const cur = sessionRef.current;
        if (!cur || cur.generationId !== msg.generationId) return;
        if (msg.status === "complete") {
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  status: "complete",
                  progress: 100,
                  message: "Complete",
                }
              : prev,
          );
          // Pull the result tree so this tab's generator/indicator match.
          if (!countTestCases(sessionRef.current?.testStructure)) {
            hydrateResult(msg.generationId);
          }
        } else {
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  status: "error",
                  error: prev.error ?? "Generation failed",
                }
              : prev,
          );
        }
        return;
      }

      if (msg.kind === "clear") {
        const cur = sessionRef.current;
        if (cur && cur.generationId === msg.generationId) {
          setSession(null);
          ownerMountedRef.current = false;
          setOwnerMountedState(false);
        }
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [hydrateResult]);

  // Stable — deps are all useCallbacks, so identity never changes across renders.
  const actions = useMemo<BgGenActions>(
    () => ({ begin, mirror, setOwnerMounted, clear, getSession }),
    [begin, mirror, setOwnerMounted, clear, getSession],
  );

  const state = useMemo<BgGenState>(
    () => ({ session, resultCount: countTestCases(session?.testStructure) }),
    [session],
  );

  return (
    <BgGenActionsContext.Provider value={actions}>
      <BgGenStateContext.Provider value={state}>
        {children}
      </BgGenStateContext.Provider>
    </BgGenActionsContext.Provider>
  );
}

/** Actions only (stable) — safe to use without re-rendering on progress ticks. */
export function useBackgroundGenerationActions(): BgGenActions | null {
  return useContext(BgGenActionsContext);
}

/** Reactive session state — re-renders on every progress tick (indicator only). */
export function useBackgroundGenerationState(): BgGenState {
  const ctx = useContext(BgGenStateContext);
  return ctx ?? { session: null, resultCount: 0 };
}
